import { useEffect, useRef, useState } from 'react';
import { Instagram, Facebook, ChevronUp, ChevronDown } from 'lucide-react';
import { DrinkVariant } from '@/config/drinkVariants';
import { useCart } from '@/context/CartContext';
import gLogo from '@/assets/g-logo.webp';

interface HeroSectionProps {
  frames: HTMLImageElement[];
  currentFrame: number;
  currentVariant: DrinkVariant;
  variantIndex: number;
  totalVariants: number;
  onPrev: () => void;
  onNext: () => void;
}

const HeroSection = ({ 
  frames, 
  currentFrame, 
  currentVariant,
  variantIndex,
  totalVariants,
  onPrev,
  onNext
}: HeroSectionProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setIsTouch(window.matchMedia('(pointer: coarse)').matches);
  }, []);

  const handleAddToCart = () => {
    addItem({
      id: `g-${currentVariant.id}`,
      name: currentVariant.name,
      subtitle: currentVariant.subtitle,
      brand: 'G Drink',
      productImage: currentVariant.productImage,
      price: currentVariant.price,
    });
  };

  // Draw current frame to canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    
    if (!canvas || !ctx) return;
    
    // Set canvas size to match window
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    const currentImage = frames[currentFrame];

    // Guard: only draw if image has valid decoded dimensions
    // This prevents drawing half-decoded or corrupted images
    if (currentImage && currentImage.naturalWidth > 0 && currentImage.naturalHeight > 0) {
      // Calculate cover dimensions
      const imgRatio = currentImage.naturalWidth / currentImage.naturalHeight;
      const canvasRatio = canvas.width / canvas.height;

      let drawWidth, drawHeight, offsetX, offsetY;

      if (canvasRatio > imgRatio) {
        drawWidth = canvas.width;
        drawHeight = canvas.width / imgRatio;
        offsetX = 0;
        offsetY = (canvas.height - drawHeight) / 2;
      } else {
        drawHeight = canvas.height;
        drawWidth = canvas.height * imgRatio;
        offsetX = (canvas.width - drawWidth) / 2;
        offsetY = 0;
      }

      ctx.drawImage(currentImage, offsetX, offsetY, drawWidth, drawHeight);
    } else {
      // Clear canvas if no valid frame - prevents stale frames from previous variant
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [frames, currentFrame]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current) {
        canvasRef.current.width = window.innerWidth;
        canvasRef.current.height = window.innerHeight;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const canGoPrev = variantIndex > 0;
  const canGoNext = variantIndex < totalVariants - 1;
  const displayIndex = (variantIndex + 1).toString().padStart(2, '0');

  return (
    <div className="fixed inset-0 w-full h-screen z-10">
      {/* Canvas background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Gradient overlay for text readability */}
      <div className="absolute inset-0 gradient-hero" />

      {/* Left side content overlay */}
      <div className="absolute inset-0 flex items-center">
        <div className="px-6 md:px-12 lg:px-16 w-full">
          <div className="max-w-lg animate-fade-in-up">
            {/* Brand */}
            <span className="text-xs font-medium tracking-[0.3em] text-muted-foreground uppercase mb-2 block">
              DLC
            </span>

            {/* Drink name */}
            <img 
              src={gLogo} 
              alt="G"
              width={482}
              height={600}
              {...({ fetchpriority: 'high' } as any)}
              decoding="async"
              className="h-[8rem] md:h-[12rem] lg:h-[16rem] w-auto -ml-2"
            />

            {/* Subtitle */}
            <p className="text-lg md:text-xl font-light tracking-[0.15em] text-primary -mt-4 mb-4">
              {currentVariant.subtitle}
            </p>

            {/* Description */}
            <p className="text-sm md:text-base text-muted-foreground max-w-sm mb-8 leading-relaxed">
              {currentVariant.description}
            </p>

            {/* CTA Buttons - tap-friendly on touch, hover-merge on desktop */}
            {isTouch ? (
              <button
                onClick={handleAddToCart}
                style={{ touchAction: 'manipulation' }}
                className="px-8 py-3 rounded-full bg-primary text-primary-foreground font-medium text-sm tracking-wider uppercase shadow-glow active:scale-95 transition-transform"
              >
                Add To Cart
              </button>
            ) : (
              <div
                className="relative"
                onMouseEnter={() => setIsButtonHovered(true)}
                onMouseLeave={() => setIsButtonHovered(false)}
              >
                <div className={`flex gap-4 transition-all duration-300 ${isButtonHovered ? 'opacity-0 scale-95' : 'opacity-100'}`}>
                  <button className="px-8 py-3 rounded-full border border-foreground/30 text-foreground font-medium text-sm tracking-wider uppercase transition-all duration-300">
                    Add To
                  </button>
                  <button className="px-8 py-3 rounded-full bg-foreground text-background font-medium text-sm tracking-wider uppercase transition-all duration-300">
                    Cart
                  </button>
                </div>
                <button
                  onClick={handleAddToCart}
                  className={`absolute top-0 left-0 px-8 py-3 rounded-full bg-primary text-primary-foreground font-medium text-sm tracking-wider uppercase transition-all duration-300 shadow-glow ${isButtonHovered ? 'opacity-100 scale-100' : 'opacity-0 pointer-events-none scale-105'}`}
                >
                  Add To Cart
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right side navigation */}
      <div className="absolute right-6 md:right-12 top-1/2 -translate-y-1/2 flex items-center gap-4">
        {/* Large index number */}
        <span className="font-display text-[6rem] md:text-[8rem] text-foreground/10">
          {displayIndex}
        </span>

        {/* Vertical navigation */}
        <div className="flex flex-col items-center gap-4">
          <button
            onClick={onPrev}
            disabled={!canGoPrev}
            className={`flex flex-col items-center gap-1 transition-colors ${
              canGoPrev 
                ? 'text-foreground hover:text-primary cursor-pointer' 
                : 'text-muted-foreground/50 cursor-not-allowed'
            }`}
          >
            <ChevronUp className="w-4 h-4" />
            <span className="text-[10px] tracking-wider uppercase">Prev</span>
          </button>

          <div className="w-px h-12 bg-foreground/20" />

          <button
            onClick={onNext}
            disabled={!canGoNext}
            className={`flex flex-col items-center gap-1 transition-colors ${
              canGoNext 
                ? 'text-foreground hover:text-primary cursor-pointer' 
                : 'text-muted-foreground/50 cursor-not-allowed'
            }`}
          >
            <span className="text-[10px] tracking-wider uppercase">Next</span>
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Bottom social icons */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-6">
        <a
          href="https://instagram.com/dlc.ps"
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-primary transition-colors"
          aria-label="Instagram"
        >
          <Instagram className="w-5 h-5" />
        </a>
        <a
          href="https://www.facebook.com/profile.php?id=100083289845118"
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-primary transition-colors"
          aria-label="Facebook"
        >
          <Facebook className="w-5 h-5" />
        </a>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-6 md:right-12 flex flex-col items-center gap-2 animate-float">
        <span className="text-[10px] tracking-wider uppercase text-muted-foreground">
          Scroll
        </span>
        <div className="w-px h-8 bg-gradient-to-b from-primary to-transparent" />
      </div>
    </div>
  );
};

export default HeroSection;
